import React, { useState, useEffect } from 'react'
import SafeIcon from '../../common/SafeIcon'
import { imageService } from '../../lib/imageService.js'
import { useAuth } from '../../contexts/AuthContext'
import creditService from '../../lib/creditService'
import * as FiIcons from 'react-icons/fi'

const { FiX, FiZap, FiCheck, FiArrowLeft, FiLoader } = FiIcons

// Camera angle options based on the provided reference image
const CAMERA_ANGLES = [
  { value: '', label: 'No specific angle' },
  { value: 'worms-eye-view', label: "Worm's Eye View (-75°)", description: 'Extreme low angle, looking up from ground level' },
  { value: 'low-view', label: 'Low View (-45°)', description: 'Low angle shot, subject appears powerful' },
  { value: 'hero-view', label: 'Hero View (-15°)', description: 'Slightly low angle, heroic perspective' },
  { value: 'straight-on', label: 'Straight On (0°)', description: 'Eye level, neutral perspective' },
  { value: 'slightly-above', label: 'Slightly Above (10°-15°)', description: 'Slight high angle, natural view' },
  { value: 'above-shot', label: 'Above Shot (30°)', description: 'High angle, subject appears smaller' },
  { value: 'high-angle-shot', label: 'High Angle Shot (45°)', description: 'Strong high angle, dramatic perspective' },
  { value: 'birds-eye-view', label: "Bird's Eye View (65°)", description: 'Looking down from above' },
  { value: 'top-down', label: 'Top Down (90°)', description: 'Directly overhead view' }
]

const SHOT_TYPES = [
  { value: '', label: 'No specific shot type' },
  { value: 'extreme-wide', label: 'Extreme Wide Shot', description: 'Vast landscape, subject very small' },
  { value: 'wide', label: 'Wide Shot', description: 'Full body visible, shows environment' },
  { value: 'medium-wide', label: 'Medium Wide Shot', description: 'Knees up, good for groups' },
  { value: 'medium', label: 'Medium Shot', description: 'Waist up, natural conversation distance' },
  { value: 'medium-close', label: 'Medium Close Up', description: 'Chest up, intimate but not too close' },
  { value: 'close-up', label: 'Close Up', description: 'Head and shoulders, emotional focus' },
  { value: 'extreme-close', label: 'Extreme Close Up', description: 'Face or specific details only' }
]

const ImageGenerationModal = ({ 
  isOpen, 
  onClose, 
  onImageGenerated,
  projectTitle = '',
  genre = '',
  elementType = 'custom' // 'cover', 'character', 'scene', 'custom'
}) => {
  const { user } = useAuth()
  const [step, setStep] = useState('prompt') // 'prompt', 'generating', 'preview'
  const [prompt, setPrompt] = useState('')
  const [cameraAngle, setCameraAngle] = useState('')
  const [shotType, setShotType] = useState('')
  const [generatedImage, setGeneratedImage] = useState(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState('')
  const [error, setError] = useState('')
  const [credits, setCredits] = useState(null)
  const [creditsLoading, setCreditsLoading] = useState(true)

  useEffect(() => {
    if (user && isOpen) {
      loadCredits()
    }
  }, [user, isOpen])

  const loadCredits = async () => {
    try {
      setCreditsLoading(true)
      const creditData = await creditService.getUserCredits(user.id)
      setCredits(creditData)
    } catch (error) {
      console.error('Error loading credits:', error)
    } finally {
      setCreditsLoading(false)
    }
  }

  const handleClose = () => {
    if (isGenerating) return // Don't allow closing during generation
    
    setStep('prompt')
    setPrompt('')
    setCameraAngle('')
    setShotType('')
    setGeneratedImage(null)
    setError('')
    setProgress('')
    onClose()
  }

  // Function to create enhanced prompt with camera details
  const createEnhancedPrompt = (basePrompt, angle, shot) => {
    let enhancedPrompt = basePrompt.trim()
    
    // Add camera angle details
    if (angle) {
      const angleData = CAMERA_ANGLES.find(a => a.value === angle)
      if (angleData) {
        enhancedPrompt += `. Camera angle: ${angleData.label} - ${angleData.description}.`
        
        // Add specific camera technical details based on angle
        switch (angle) {
          case 'worms-eye-view':
            enhancedPrompt += ' Camera positioned at ground level, extreme low perspective, creates dramatic size distortion and powerful presence.'
            break
          case 'low-view':
            enhancedPrompt += ' Camera below subject eye level, creates sense of dominance and power, slightly tilted upward.'
            break
          case 'hero-view':
            enhancedPrompt += ' Camera slightly below subject, flattering angle that makes subject appear confident and heroic.'
            break
          case 'straight-on':
            enhancedPrompt += ' Camera at subject eye level, neutral and natural perspective, balanced composition.'
            break
          case 'slightly-above':
            enhancedPrompt += ' Camera slightly above subject, natural and flattering downward angle.'
            break
          case 'above-shot':
            enhancedPrompt += ' Camera positioned above subject, creates vulnerability and shows more of the environment.'
            break
          case 'high-angle-shot':
            enhancedPrompt += ' Camera high above subject, dramatic downward perspective, subject appears smaller and more vulnerable.'
            break
          case 'birds-eye-view':
            enhancedPrompt += ' Camera looking down from high above, aerial perspective showing subject and surroundings.'
            break
          case 'top-down':
            enhancedPrompt += ' Camera directly overhead, perfect top-down view, symmetrical and geometric composition.'
            break
        }
      }
    }
    
    // Add shot type details
    if (shot) {
      const shotData = SHOT_TYPES.find(s => s.value === shot)
      if (shotData) {
        enhancedPrompt += ` Shot type: ${shotData.label} - ${shotData.description}.`
        
        // Add framing and composition details
        switch (shot) {
          case 'extreme-wide':
            enhancedPrompt += ' Frame shows vast environment, subject is small element within large landscape, establishing shot feel.'
            break
          case 'wide':
            enhancedPrompt += ' Frame shows full body and significant environment, good for action and context.'
            break
          case 'medium-wide':
            enhancedPrompt += ' Frame from knees up, shows body language and some environment, good for groups.'
            break
          case 'medium':
            enhancedPrompt += ' Frame from waist up, natural conversation distance, focuses on upper body and expressions.'
            break
          case 'medium-close':
            enhancedPrompt += ' Frame from chest up, intimate framing that emphasizes facial expressions and emotions.'
            break
          case 'close-up':
            enhancedPrompt += ' Frame shows head and shoulders, emotional focus on face, minimal background distraction.'
            break
          case 'extreme-close':
            enhancedPrompt += ' Frame shows face only or specific details, intense emotional impact, very tight framing.'
            break
        }
      }
    }
    
    // Add general cinematography enhancement
    enhancedPrompt += ' Professional cinematography, film-quality lighting and composition.'
    
    return enhancedPrompt
  }

  const handleGenerate = async () => {
    if (!prompt.trim() || !user) return

    // Check if user has enough image credits
    if (!credits || credits.imageCredits < 1) {
      setError('You need at least 1 AI Image Generation credit to generate images. Please upgrade your plan or wait for your monthly quota to reset.')
      return
    }

    setIsGenerating(true)
    setStep('generating')
    setError('')

    try {
      // Consume image generation credit
      const creditConsumed = await creditService.consumeCredits(
        user.id,
        'image',
        1,
        'AI image generation in editor'
      )

      if (!creditConsumed) {
        throw new Error('Unable to consume image generation credit. Please try again.')
      }

      const enhancedPrompt = createEnhancedPrompt(prompt, cameraAngle, shotType)
      
      const imageData = await imageService.generateImage({
        prompt: enhancedPrompt,
        type: elementType,
        userId: user.id,
        projectTitle,
        genre,
        onProgress: (progressMessage) => {
          setProgress(progressMessage)
        }
      })

      setGeneratedImage(imageData)
      setStep('preview')
      setProgress('Image generated successfully!')
      
      // Refresh credits after successful generation
      await loadCredits()

    } catch (err) {
      console.error('Image generation failed:', err)
      setError(err.message || 'Failed to generate image')
      setStep('prompt')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleAcceptImage = () => {
    if (generatedImage) {
      onImageGenerated(generatedImage)
      handleClose()
    }
  }

  const handleBackToPrompt = () => {
    setStep('prompt')
    setGeneratedImage(null)
    setError('')
  }

  const getPlaceholderText = () => {
    switch (elementType) {
      case 'cover':
        return `Describe the poster for "${projectTitle}". Include mood, style, key visual elements...`
      case 'character':
        return 'Describe the character: appearance, clothing, setting, mood...'
      case 'scene':
        return 'Describe the scene: location, atmosphere, action, lighting...'
      default:
        return 'Describe the image you want to generate...'
    }
  }

  const getModalTitle = () => {
    switch (elementType) {
      case 'cover':
        return 'Generate Cover Poster'
      case 'character':
        return 'Generate Character Image'
      case 'scene':
        return 'Generate Scene Image'
      default:
        return 'Generate AI Image'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {getModalTitle()}
            </h3>
            {!creditsLoading && credits && (
              <div className="flex items-center space-x-1 text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                <span>🎨</span>
                <span className={credits.imageCredits > 0 ? 'text-green-600' : 'text-red-600'}>
                  {credits.imageCredits}
                </span>
                <span className="text-gray-400">credits</span>
              </div>
            )}
          </div>
          {!isGenerating && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Prompt Step */}
          {step === 'prompt' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image Description
                </label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={getPlaceholderText()}
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">
                  Be specific about style, mood, colors, and visual elements for best results.
                </p>
              </div>

              {/* Camera Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Camera Angle
                  </label>
                  <select
                    value={cameraAngle}
                    onChange={(e) => setCameraAngle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                  >
                    {CAMERA_ANGLES.map((angle) => (
                      <option key={angle.value} value={angle.value}>
                        {angle.label}
                      </option>
                    ))}
                  </select>
                  {cameraAngle && (
                    <p className="text-xs text-gray-500 mt-1">
                      {CAMERA_ANGLES.find(a => a.value === cameraAngle)?.description}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shot Type
                  </label>
                  <select
                    value={shotType}
                    onChange={(e) => setShotType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                  >
                    {SHOT_TYPES.map((shot) => (
                      <option key={shot.value} value={shot.value}>
                        {shot.label}
                      </option>
                    ))}
                  </select>
                  {shotType && (
                    <p className="text-xs text-gray-500 mt-1">
                      {SHOT_TYPES.find(s => s.value === shotType)?.description}
                    </p>
                  )}
                </div>
              </div>

              {(cameraAngle || shotType) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-blue-800 text-sm">
                    <strong>Enhanced prompt will include:</strong>
                    {cameraAngle && ` ${CAMERA_ANGLES.find(a => a.value === cameraAngle)?.label}`}
                    {cameraAngle && shotType && ' + '}
                    {shotType && ` ${SHOT_TYPES.find(s => s.value === shotType)?.label}`}
                  </p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={!prompt.trim()}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <SafeIcon icon={FiZap} className="w-4 h-4" />
                  <span>Generate Image</span>
                </button>
              </div>
            </div>
          )}

          {/* Generating Step */}
          {step === 'generating' && (
            <div className="text-center py-8">
              <div className="flex justify-center mb-4">
                <SafeIcon icon={FiLoader} className="w-12 h-12 text-blue-600 animate-spin" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-2">
                Generating Your Image...
              </h4>
              <p className="text-gray-600 mb-4">
                This usually takes 10-30 seconds
              </p>
              {progress && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-blue-800 text-sm">{progress}</p>
                </div>
              )}
            </div>
          )}

          {/* Preview Step */}
          {step === 'preview' && generatedImage && (
            <div className="space-y-4">
              <div className="text-center">
                <img
                  src={generatedImage.url}
                  alt="Generated image"
                  className="max-w-full max-h-96 mx-auto rounded-lg shadow-lg"
                  style={{ objectFit: 'contain' }}
                />
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  <strong>Generated with prompt:</strong> {generatedImage.prompt}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Dimensions: {generatedImage.width} × {generatedImage.height}px
                </p>
                {(cameraAngle || shotType) && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-600">
                      <strong>Camera settings used:</strong>
                      {cameraAngle && ` ${CAMERA_ANGLES.find(a => a.value === cameraAngle)?.label}`}
                      {cameraAngle && shotType && ' + '}
                      {shotType && ` ${SHOT_TYPES.find(s => s.value === shotType)?.label}`}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={handleBackToPrompt}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
                  <span>Generate Different</span>
                </button>

                <div className="flex space-x-3">
                  <button
                    onClick={handleClose}
                    className="flex items-center space-x-2 px-4 py-2 text-red-700 hover:text-red-900 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                    <span>Discard</span>
                  </button>
                  <button
                    onClick={handleAcceptImage}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <SafeIcon icon={FiCheck} className="w-4 h-4" />
                    <span>Use This Image</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ImageGenerationModal
